import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { FitAppConfig } from './fit-app-config';
import { emptyInAppPromoCodeAvailability, InAppPromoCodeAvailability } from './in-app-promo-code-availability';

export interface SidebarNavigationItem {
    url?: string;
    title?: string;
    homeTitle?: string;
    subtitle?: string;
    description?: string;
    color?: string;
    icon?: string;
    iconHome?: string;
    opened?: boolean;
    hideOnHome?: boolean;
    items?: SidebarNavigationItem[];
}

export interface SidebarNavigation {
    title: string;
    subtitle?: string;
    color: string;
    icon: string;
    iconHome: string;
    opened: boolean;
    url?: string;
    items?: SidebarNavigationItem[];
}

export interface Country {
    value: string;
    group: string;
    text: string;
}

@Injectable({ providedIn: 'root' })

export class FitConfigProviderService {
    public static settings: FitAppConfig;

    get navigation() {
        return this._navigation;
    }

    get apiUrl() {
        return FitConfigProviderService.settings.apiUrl;
    }
    
    get dateFormat() {
        return FitConfigProviderService.settings.format.date;
    }
    
    get timeFormat() {
        return FitConfigProviderService.settings.format.time;
    }

    get dateTimeFormat() {
        return FitConfigProviderService.settings.format.dateTime;
    }

    get userDateFormat() {
        return this._userDateFormat;
    }

    set userDateFormat(value) {
        this._userDateFormat = value;
    }

    get pinApp() {
        return this._pinApp;
    }
    
    set pinApp(value) {
        this._pinApp = value;
    }

    get workoutReadinessQuestionnaireId() {
        return this._workoutReadinessQuestionnaireId;
    }

    set workoutReadinessQuestionnaireId(value) {
        this._workoutReadinessQuestionnaireId = value;
    }

    get programReadinessQuestionnaireId() {
        return this._programReadinessQuestionnaireId;
    }

    set programReadinessQuestionnaireId(value) {
        this._programReadinessQuestionnaireId = value;
    }

    set enableUserHealthTracking(value) {
        this._enableUserHealthTracking = value;
    }

    get enableUserHealthTracking() {
        return this._enableUserHealthTracking;
    }

    set enableClientMessaging(value) {
        this._enableClientMessaging = value;
    }

    get enableClientMessaging() {
        return this._enableClientMessaging;
    }

    set enableGroupChat(value) {
        this._enableGroupChat = value;
    }

    get enableGroupChat() {
        return this._enableGroupChat;
    }

    set isBlockedFromPosting(value) {
        this._isBlockedFromPosting = value;
    }

    get isBlockedFromPosting() {
        return this._isBlockedFromPosting;
    }

    set enableScheduler(value) {
        this._enableScheduler = value;
    }

    get enableScheduler() {
        return this._enableScheduler;
    }

    set enableReferrals(value) {
        this._enableReferrals = value;
    }

    get enableReferrals() {
        return this._enableReferrals;
    }

    set enableChallenges(value) {
        this._enableChallenges = value;
    }

    get enableChallenges() {
        return this._enableChallenges;
    }

    set enableCoaching(value) {
        this._enableCoaching = value;
    }

    get enableCoaching() {
        return this._enableCoaching;
    }

    set enablePaidProgramAssignment(value) {
        this._enablePaidProgramAssignment = value;
    }

    get enablePaidProgramAssignment() {
        return this._enablePaidProgramAssignment;
    }

    set enableSettingDefaultUserFolder(value) {
        this._enableSettingDefaultUserFolder = value;
    }

    get enableSettingDefaultUserFolder() {
        return this._enableSettingDefaultUserFolder;
    }

    set enableCoachingCheckoutPagesSetup(value) {
        this._enableCoachingCheckoutPagesSetup = value;
    }

    get enableCoachingCheckoutPagesSetup() {
        return this._enableCoachingCheckoutPagesSetup;
    }

    set ownerWorkoutsAreHiddenFromCoaches(value) {
        this._ownerWorkoutsAreHiddenFromCoaches = value;
    }

    get ownerWorkoutsAreHiddenFromCoaches() {
        return this._ownerWorkoutsAreHiddenFromCoaches;
    }

    set allowHidingCourseLinksFromTeamMembers(value) {
        this._allowHidingCourseLinksFromTeamMembers = value;
    }

    get allowHidingCourseLinksFromTeamMembers() {
        return this._allowHidingCourseLinksFromTeamMembers;
    }

    set ownerProgramsAreHiddenFromCoaches(value) {
        this._ownerProgramsAreHiddenFromCoaches = value;
    }

    get ownerProgramsAreHiddenFromCoaches() {
        return this._ownerProgramsAreHiddenFromCoaches;
    }

    set allowCoachesToManageOwnTeams(value) {
        this._allowCoachesToManageOwnTeams = value;
    }

    get allowCoachesToManageOwnTeams() {
        return this._allowCoachesToManageOwnTeams;
    }

    set enableTeams(value) {
        this._enableTeams = value;
    }

    get enableTeams() {
        return this._enableTeams;
    }

    set enableGiftCards(value) {
        this._enableGiftCards = value;
    }

    get enableGiftCards() {
        return this._enableGiftCards;
    }

    set inAppPromoCodesAvailability(value: InAppPromoCodeAvailability | null | undefined) {
        this._inAppPromoCodesAvailability = value ?? emptyInAppPromoCodeAvailability();
    }

    get inAppPromoCodesAvailability(): InAppPromoCodeAvailability {
        return this._inAppPromoCodesAvailability;
    }

    get inAppPromoCodesAvailable(): boolean {
        return this._inAppPromoCodesAvailability.available;
    }

    set enableAppTryout(value) {
        this._enableAppTryout = value;
    }

    get enableAppTryout() {
        return this._enableAppTryout;
    }

    set enableReferralCodes(value) {
        this._enableReferralCodes = value;
    }

    get enableReferralCodes() {
        return this._enableReferralCodes;
    }

    set enableUserProgression(value) {
        this._enableUserProgression = value;
    }

    get enableUserProgression() {
        return this._enableUserProgression;
    }

    set enableOutgoingWebhooks(value) {
        this._enableOutgoingWebhooks = value;
    }

    get enableOutgoingWebhooks() {
        return this._enableOutgoingWebhooks;
    }

    get isAdministrator() {
        return this._isAdministrator;
    }

    set isAdministrator(value) {
        this._isAdministrator = value;
    }

    get isCoach() {
        return this._isCoach;
    }

    set isCoach(value) {
        this._isCoach = value;
        if (!this.isAdministrator && this._isCoach) {
            //this._navigation = this.enableScheduler ? this.getCoachNavigation() : [];
            this._navigation = this.getCoachNavigation();
            
            if (this.enableTeams) {
               /*
                const index = this._navigation[0].items.indexOf(this.navigation[0].items.find(item => item.title === 'All users'))
                this._navigation[0].items.splice(index, 0, {
                    title: 'Teams',
                    url: '/team',
                });
                */
                this._navigation[0].items.push({
                    title: 'navigation_teams',
                    url: '/team',
                });
            }

            if (this.enableScheduler) {
                this._navigation.push({
                    title: 'navigation_scheduler',
                    color: '#C2F1FF',
                    icon: 'assets/icons/ic_website.svg',
                    iconHome: 'assets/icons/ic_website_home.svg',
                    opened: false,
                    items: [
                        {
                            title: 'navigation_calendar',
                            url: '/scheduler/calendar',
                        },
                        {
                            title: 'navigation_calendar_settings',
                            url: '/scheduler/calendar-settings',
                        },
                        {
                            title: 'navigation_event_types',
                            url: '/scheduler/event-types',
                        },
                    ],
                },);
            }

            if (this.enableClientMessaging || this.enableGroupChat) {
                let schedulerIndex = this._navigation.indexOf(this.navigation.find(item => item.title === 'navigation_scheduler'))
                const index = schedulerIndex !== -1 ? schedulerIndex : this.navigation.length;

                this._navigation.splice(index, 0, {
                    title: 'navigation_messages',
                    color: '#C1F1C6',
                    icon: 'assets/icons/ic_messages.svg',
                    iconHome: 'assets/icons/ic_messages_home.svg',
                    opened: false,
                    items: [],
                });

                if (this.enableClientMessaging) {
                    this._navigation[index].items.push({
                        title: 'navigation_direct_messages',
                        url: '/client-messages',
                    });
                }

                if (this.enableGroupChat) {
                    this._navigation[index].items.push({
                        title: 'navigation_chat_rooms',
                        url: '/chat-rooms',
                    });
                }
            }
        }
    }

    get isOwner() {
        return this._isOwner;
    }

    set isOwner(value) {
        this._isOwner = value;
    }

    set isRoot(value) {
        this._isRoot = value;
        const appPin = localStorage.getItem('pin_application');
        this._navigation = appPin ? this.getAdminNavigation() : this.getMainCmsNavigation();

        let workoutsIndex = this._navigation.findIndex(item => item.title === 'navigation_workout');
        let nutritionIndex = this._navigation.findIndex(item => item.title === 'navigation_nutrition');
        let appFeaturesIndex = this._navigation.findIndex(item => item.title === 'navigation_app_features');
        let myClientsIndex = this._navigation.findIndex(item => item.title === 'navigation_my_clients');
        let appAnalyticsIndex = this._navigation.findIndex(item => item.title === 'navigation_app_analytics');
        let appPromotionIndex = this._navigation.findIndex(item => item.title === 'navigation_app_promotion');
        let appSetupIndex = this._navigation.findIndex(item => item.title === 'navigation_app_setup');
        let webSiteIndex = this._navigation.findIndex(item => item.title === 'navigation_web_site');
        
        if (this._isRoot) {
            if (appPin) {
                //extends admin navigation

                this._navigation[myClientsIndex].items.push({
                    title: 'navigation_packages',
                    url: '/packages',
                });

                this._navigation[myClientsIndex].items.push({
                    title: 'navigation_package_tiers',
                    url: '/package-tiers',
                });

                this._navigation[appSetupIndex].items.push({
                    title: 'navigation_customize_cms_text',
                    url: '/cms-translate',
                });

                //root can see whole app analytics
                
                this._navigation[appAnalyticsIndex].items.push({
                    title: 'navigation_statistics',
                    url: '/statistics',
                });

                this._navigation[appAnalyticsIndex].items.push({
                    title: 'navigation_reports',
                    url: '/statistics/reports',
                });

                this._navigation[appAnalyticsIndex].items.push({
                    title: 'navigation_content_analytics',
                    url: '/business-intelligence',
                });
            }
        } else {
            if (this.enableScheduler && (this._isOwner || this._isCoach)) {
                const index = this._navigation[appFeaturesIndex].items.indexOf(this.navigation[appFeaturesIndex].items.find(item => item.title === 'navigation_live_events'))
                this._navigation[appFeaturesIndex].items.splice(index, 0, {
                    title: 'navigation_scheduler',
                    color: '#0B2441',
                    icon: 'assets/icons/circle_section_promotion_cms.svg',
                    iconHome: 'assets/icons/circle_section_promotion_cms.svg',
                    opened: false,
                    items: [
                        {
                            title: 'navigation_calendar',
                            url: '/scheduler/calendar',
                        },
                        {
                            title: 'navigation_calendar_settings',
                            url: '/scheduler/calendar-settings',
                        },
                        {
                            title: 'navigation_event_types',
                            url: '/scheduler/event-types',
                        },
                    ],
                });
            }

            if (appPin) {
                if (appPin == '4o2e34' || appPin == '79891' || appPin == '8a2a3' || appPin == '651b9') {
                    //we are displaying whole app analytics to Mariah, Golf STRONG, SW7 and Zania
                    this._navigation[appAnalyticsIndex].items.push({
                        title: 'navigation_statistics',
                        url: '/statistics',
                    });
        
                    this._navigation[appAnalyticsIndex].items.push({
                        title: 'navigation_reports',
                        url: '/statistics/reports',
                    });
        
                    this._navigation[appAnalyticsIndex].items.push({
                        title: 'navigation_content_analytics',
                        url: '/business-intelligence',
                    });
                } else {
                    //others can see only content analytics
                    this._navigation[appAnalyticsIndex].items.push({
                        title: 'navigation_content_analytics',
                        url: '/business-intelligence',
                    });
                }
            }
        }

        if (appPin) {
            if (appPin == '4o2e34') {
                const nutritionSetupIndex = this._navigation[nutritionIndex].items.findIndex(item => item.title === 'navigation_nutrition_setup');
                if (nutritionSetupIndex !== -1) {
                    this._navigation[nutritionIndex].items[nutritionSetupIndex].items.push({
                        title: 'navigation_ingredients',
                        url: '/ingredient',
                    });

                    this._navigation[nutritionIndex].items[nutritionSetupIndex].items.push({
                        title: 'navigation_ingredient_groups',
                        url: '/ingredient-group',
                    });
                }
            }

            if (this.enableTeams) {
                const index = this._navigation[myClientsIndex].items.indexOf(this.navigation[myClientsIndex].items.find(item => item.title === 'navigation_comments'))
                this._navigation[myClientsIndex].items.splice(index, 0, {
                    title: 'navigation_teams',
                    url: '/team',
                });
            }

            const index = this._navigation[myClientsIndex].items.indexOf(this.navigation[myClientsIndex].items.find(item => item.title === 'navigation_subscriptions'))
            this._navigation[myClientsIndex].items.splice(index, 0, {
                title: 'navigation_tailor_requests',
                url: '/plan-requests',
            });

            const index2 = this._navigation[appSetupIndex].items.indexOf(this.navigation[appSetupIndex].items.find(item => item.title === 'navigation_customize_text'))
            this._navigation[appSetupIndex].items.splice(index2, 0, {
                title: 'navigation_customize_app',
                url: '/mobile-config',
            });

            if (this.enableGiftCards) {
                this._navigation[appPromotionIndex].items.push({
                    title: 'navigation_gift_cards',
                    url: '/gift-card',
                });
            }

            if (this.enableAppTryout) {
                this._navigation[appPromotionIndex].items.push({
                    title: 'navigation_app_tryout',
                    url: '/app-tryout',
                });
            }

            if (this.enableReferrals) {
                this._navigation[appPromotionIndex].items.push({
                    title: 'navigation_referral_program',
                    url: '/referral-code',
                });
            }

            if (this.enableReferralCodes) {
                this._navigation[appPromotionIndex].items.push({
                    title: 'navigation_referral_codes',
                    opened: false,
                    hideOnHome: true,
                    items: [
                        {
                            title: 'navigation_how_it_works',
                            homeTitle: 'navigation_referral_codes_how_it_works',
                            url: '/referral-code-instructions',
                        },
                    ],
                });
            }

            if (this.enableChallenges) {
                this._navigation[appFeaturesIndex].items.push({
                    title: 'navigation_challenges',
                    url: '/challenge',
                });
            }

            this._navigation.push({
                title: 'navigation_web_site',
                color: '#C2F1FF',
                icon: 'assets/icons/ic_website.svg',
                iconHome: 'assets/icons/ic_website_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_overview',
                        url: '/landing-page',
                    },
                    {
                        title: 'navigation_customize_layout',
                        url: '/landing-page/app-features',
                    },
                ]
            });

            if (this.enableCoaching) {
                const index = this._navigation.indexOf(this.navigation.find(item => item.title === 'navigation_app_features'))
                this._navigation.splice(index, 0, {
                    title: 'navigation_coaching',
                    color: '#C1F1C6',
                    icon: 'assets/icons/ic_coaching.svg',
                    iconHome: 'assets/icons/ic_coaching_home.svg',
                    opened: false,
                    items: [
                        {
                            title: 'navigation_coaching_lessons',
                            url: '/lesson',
                        },
                        {
                            title: 'navigation_coaching_categories',
                            url: '/course',
                        },
                        {
                            title: 'navigation_category_groups',
                            url: '/course-category',
                        },
                        {
                            subtitle: 'SETUP',
                            opened: false,
                            hideOnHome: true,
                            title: 'navigation_coaching_setup',
                            items: [
                                {
                                    title: 'navigation_lesson_types',
                                    homeTitle: 'navigation_lesson_types',
                                    url: '/lesson-type',
                                },
                                {
                                    title: 'navigation_lesson_goals',
                                    homeTitle: 'navigation_lesson_goals',
                                    url: '/lesson-goal',
                                },
                                {
                                    title: 'navigation_achievements',
                                    homeTitle: 'navigation_achievements',
                                    url: '/lesson-achievement',
                                },

                            ],
                        },
                    ],
                });

                if (this.enableSettingDefaultUserFolder) {
                    const index2 = this._navigation[index].items.findIndex(item => item.title === 'navigation_coaching_setup');
                    if (index2 !== -1) {
                        this._navigation[index].items[index2].items.push({
                            title: 'navigation_default_user_folder',
                            url: '/default-user-folder',
                        });
                    }
                }
            }

            if (this.enableClientMessaging || this.enableGroupChat) {
                const index = this._navigation.indexOf(this.navigation.find(item => item.title === 'navigation_app_analytics'))

                this._navigation.splice(index, 0, {
                    title: 'navigation_messages',
                    color: '#C1F1C6',
                    icon: 'assets/icons/ic_messages.svg',
                    iconHome: 'assets/icons/ic_messages_home.svg',
                    opened: false,
                    items: [],
                });

                if (this.enableClientMessaging) {
                    this._navigation[index].items.push({
                        title: 'navigation_direct_messages',
                        url: '/client-messages',
                    });
                }

                if (this.enableGroupChat) {
                    this._navigation[index].items.push({
                        title: 'navigation_chat_rooms',
                        url: '/chat-rooms',
                    });
                }

                if (this.enableClientMessaging) {
                    this._navigation[index].items.push({
                        title: 'navigation_broadcast_messages',
                        url: '/broadcast-messages',
                    });
                }

                if (this.enableGroupChat) {
                    this._navigation[index].items.push({
                        title: 'navigation_reported_messages',
                        url: '/reported-messages',
                    });
                }
            }

            if (this.enableOutgoingWebhooks) {
                this._navigation.push({
                    title: 'navigation_outgoing_webhooks',
                    color: '#F8BBD0',
                    icon: 'assets/icons/ic_library.svg',
                    iconHome: 'assets/icons/ic_library_home.svg',
                    opened: false,
                    items: [
                        {
                            title: 'navigation_webhook_events',
                            url: '/outgoing-webhooks',
                        },
                    ]
                });
            }
        }
        
        this._navigation.push();
    }

    get isRoot() {
        return this._isRoot;
    }

    public async getConfig() {
        const jsonFile = 'assets/config/config.json';
        return new Promise<FitAppConfig>((resolve, reject) => {
            if (FitConfigProviderService.settings) {
                resolve(FitConfigProviderService.settings);
            } else {
                fetch(jsonFile)
                    .then((res) => {
                        if (res.headers.has('x-white-label-application-pin')) {
                            this.pinApp = res.headers.get(
                                'x-white-label-application-pin'
                            );
                        }
                        return res.json();
                    })
                    .then((response: FitAppConfig) => {
                        FitConfigProviderService.settings = response as FitAppConfig;
                        resolve(response);
                    })
                    .catch((response: any) => {
                        reject(
                            `Could not load file '${jsonFile}': ${JSON.stringify(
                                response
                            )}`
                        );
                    });
            }
        });
    }

    public getCountryList() {
        return new Promise<Country[]>((resolve, reject) => {
            fetch('assets/config/countries.json')
                .then(res => res.json())
                .then(countries => resolve(countries))
                .catch(() => reject('Error loading list of countries'))
        });
    }

    private getMainCmsNavigation(): SidebarNavigation[] {
        return [
            {
                title: 'navigation_my_clients',
                color: '#E4D6F6',
                icon: 'assets/icons/ic_my_clients.svg',
                iconHome: 'assets/icons/ic_my_clients_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_all_users',
                        url: '/user/user',
                    },
                ],
            },
            {
                title: 'navigation_app_analytics',
                color: '#FFDBD7',
                icon: 'assets/icons/ic_app_statistics.svg',
                iconHome: 'assets/icons/ic_app_statistics_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_statistics',
                        url: '/statistics',
                    },
                    {
                        title: 'navigation_reports',
                        url: '/statistics/reports',
                    },
                    {
                        title: 'navigation_content_analytics',
                        url: '/business-intelligence',
                    },
                ],
            },
            {
                title: 'navigation_applications',
                color: '#FFCCBC',
                icon: 'assets/icons/ic_library.svg',
                iconHome: 'assets/icons/ic_library_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_applications',
                        url: '/applications',
                    },
                    {
                        title: 'navigation_applications_insights',
                        url: '/app-insight',
                    },
                    {
                        title: 'navigation_user_search',
                        url: '/user-search',
                    },
                    {
                        title: 'navigation_recordings',
                        url: '/recording',
                    },
                    {
                        title: 'navigation_coach_packages',
                        url: '/coach-packages',
                    },
                ],
            },
            {
                title: 'navigation_library',
                color: '#F8BBD0',
                icon: 'assets/icons/ic_library.svg',
                iconHome: 'assets/icons/ic_library_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_library_exercises',
                        url: '/exercise-library',
                    },
                ],
            }
        ];
    }

    private getAdminNavigation(): SidebarNavigation[] {
        return [
            {
                title: 'navigation_workout',
                color: '#CFDAF6',
                icon: 'assets/icons/ic_workout.svg',
                iconHome: 'assets/icons/ic_workout_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_exercises',
                        url: '/exercise',
                        color: '#CFDAF6',
                        icon: 'assets/icons/ic_exercises.svg',
                        iconHome: 'assets/icons/ic_exercises_home.svg',
                        description: 'subtitles_exercises'
                    },
                    {
                        title: 'navigation_workouts',
                        url: '/training',
                        color: '#CFDAF6',
                        icon: 'assets/icons/ic_workouts.svg',
                        iconHome: 'assets/icons/ic_workouts_home.svg',
                        description: 'subtitles_workouts'
                    },
                    {
                        subtitle: 'SETUP',
                        title: 'navigation_program_setup',
                        opened: false,
                        hideOnHome: true,
                        items: [
                            {
                                title: 'navigation_programs',
                                url: '/program',
                            },
                            {
                                title: 'navigation_program_categories',
                                url: '/program-category',
                            },
                            {
                                title: 'navigation_program_list_latest',
                                url: '/program-list/latest',
                            }
                        ],
                    },
                    {
                        title: 'navigation_workout_setup_page',
                        url: '/workout-setup',
                    },
                    {
                        title: 'navigation_tailored_programs',
                        url: '/program-tailor-made',
                        color: '#CFDAF6',
                        icon: 'assets/icons/ic_tailored_programs.svg',
                        iconHome: 'assets/icons/ic_tailored_programs_home.svg',
                        description: 'subtitles_tailored_programs'
                    },
                    {
                        subtitle: 'LISTS',
                        title: 'navigation_workout_lists',
                        opened: false,
                        hideOnHome: true,
                        items: [
                            {
                                title: 'navigation_workout_list_preferred',
                                url: '/training-list/by_preferences',
                            },
                            {
                                title: 'navigation_workout_list_featured',
                                url: '/training-list/featured',
                            },
                            {
                                title: 'navigation_workout_list_latest',
                                url: '/training-list/latest',
                            },
                            {
                                title: 'navigation_workout_list_trending',
                                url: '/training-list/trending',
                            },
                            {
                                title: 'navigation_workout_list_promoted',
                                url: '/training-list/promoted',
                            },
                            {
                                title: 'navigation_workout_list_favorite',
                                url: '/training-list/favorite',
                            },
                            {
                                title: 'navigation_muscle_group_list_by_trainings',
                                url: '/muscle-group-list/by_trainings',
                            },
                            {
                                title: 'navigation_training_duration_list',
                                url: '/training-duration-list',
                            },
                        ],
                    },
                    {
                        subtitle: 'GROUPS',
                        title: 'navigation_workout_groups',
                        opened: false,
                        hideOnHome: true,
                        items: [
                            {
                                title: 'navigation_workout_main_groups',
                                url: '/training-group/main',
                            },
                            {
                                title: 'navigation_workout_subgroups',
                                url: '/training-group/subgroup',
                            },
                        ],
                    },
                    {
                        subtitle: 'SETUP',
                        title: 'navigation_workout_setup',
                        opened: false,
                        hideOnHome: true,
                        items: [
                            {
                                title: 'navigation_workout_instructors',
                                url: '/instructor',
                            },
                        ],
                    },
                ]
            },
            {
                title: 'navigation_nutrition',
                color: '#F6E5CF',
                icon: 'assets/icons/ic_nutrition.svg',
                iconHome: 'assets/icons/ic_nutrition_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_recipes',
                        url: '/meal',
                        color: '#F6E5CF',
                        icon: 'assets/icons/ic_recipes.svg',
                        iconHome: 'assets/icons/ic_recipes_home.svg',
                        description: 'subtitles_recipes'
                    },
                    {
                        subtitle: 'SETUP',
                        title: 'navigation_recipe_book_setup',
                        opened: false,
                        hideOnHome: true,
                        items: [
                            {
                                title: 'navigation_recipe_books',
                                url: '/nutrition-program',
                            },
                            {
                                title: 'navigation_recipe_book_categories',
                                url: '/nutrition-program-category',
                            },
                        ],
                    },
                    {
                        title: 'navigation_tailored_books',
                        url: '/nutrition-program-tailor-made',
                        color: '#F6E5CF',
                        icon: 'assets/icons/ic_tailored_books.svg',
                        iconHome: 'assets/icons/ic_tailored_books_home.svg',
                        description: 'subtitles_tailored_books'
                    },
                    {
                        subtitle: 'SETUP',
                        opened: false,
                        hideOnHome: true,
                        title: 'navigation_nutrition_setup',
                        items: [
                            {
                                title: 'navigation_meal_groups',
                                url: '/meal-group',
                            },
                            {
                                title: 'navigation_diets',
                                url: '/diet',
                            },
                            {
                                title: 'navigation_food_preferences',
                                url: '/food-preference',
                            },
                            {
                                title: 'navigation_food_allergens',
                                url: '/allergen',
                            },
                            /*
                            {
                                title: 'navigation_ingredients',
                                url: '/ingredient',
                            },
                            {
                                title: 'navigation_ingredient_groups',
                                url: '/ingredient-group',
                            }
                            */
                        ],
                    },
                ]
            },
            {
                title: 'navigation_app_features',
                color: '#C3D5FF',
                icon: 'assets/icons/ic_app_features.svg',
                iconHome: 'assets/icons/ic_app_features_home.svg',
                opened: false,
                items: [
                    {
                        subtitle: 'subtitles_setup',
                        opened: false,
                        hideOnHome: true,
                        title: 'navigation_questionnaires',
                        items: [
                            {
                                title: 'navigation_questionnaires_created_by_you',
                                homeTitle: 'navigation_questionnaires',
                                url: '/questionnaire',
                            },
                            {
                                title: 'navigation_questionnaires_completed_by_clients',
                                homeTitle: 'navigation_questionnaire_answers',
                                url: '/finished-questionnaires',
                            },
                        ],
                    },
                    {
                        title: 'navigation_live_events',
                        url: '/live-events',
                    },
                    {
                        title: 'navigation_blog',
                        url: '/blog',
                    },
                    {
                        title: 'navigation_feed',
                        url: '/feed',
                    },
                    {
                        title: 'navigation_achievements',
                        url: '/achievements',
                    },
                ],
            },
            {
                title: 'navigation_my_clients',
                color: '#E4D6F6',
                icon: 'assets/icons/ic_my_clients.svg',
                iconHome: 'assets/icons/ic_my_clients_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_all_users',
                        url: '/user/user',
                    },
                    {
                        title: 'navigation_comments',
                        url: '/review/comment',
                    },
                    {
                        title: 'navigation_subscriptions',
                        url: '/subscription',
                    },
                    {
                        title: 'navigation_payments',
                        url: '/payment',
                    },
                ],
            },
            {
                title: 'navigation_app_analytics',
                color: '#FFDBD7',
                icon: 'assets/icons/ic_app_statistics.svg',
                iconHome: 'assets/icons/ic_app_statistics_home.svg',
                opened: false,
                items: [
                    /*
                    {
                        title: 'Statistics',
                        url: '/statistics',
                    },
                    {
                        title: 'Reports',
                        url: '/statistics/reports',
                    },
                    {
                        title: 'Content analytics',
                        url: '/business-intelligence',
                    },
                    */
                ],
            },
            {
                title: 'navigation_app_promotion',
                color: '#CFE4DD',
                icon: 'assets/icons/ic_app_promotion.svg',
                iconHome: 'assets/icons/ic_app_promotion_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_products',
                        url: '/insight',
                    },
                    {
                        title: 'navigation_affiliate_partners',
                        url: '/affiliate-partner',
                    },
                    {
                        title: 'navigation_promo_codes',
                        url: '/promo-codes',
                    },
                ],
            },
            {
                title: 'navigation_app_setup',
                color: '#D5DCE9',
                icon: 'assets/icons/ic_app_setup.svg',
                iconHome: 'assets/icons/ic_app_setup_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_customize_text',
                        url: '/translate',
                    },
                    {
                        title: 'navigation_overlay_app_menu',
                        url: '/app-menu/overlay',
                    },
                    {
                        title: 'navigation_settings_app_menu',
                        url: '/app-menu/settings',
                    }
                ]
            },
        ];
    }

    private getCoachNavigation(): SidebarNavigation[] {
        return [
            {
                title: 'navigation_my_clients',
                color: '#E4D6F6',
                icon: 'assets/icons/ic_my_clients.svg',
                iconHome: 'assets/icons/ic_my_clients_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_all_users',
                        url: '/user/user',
                    },
                    {
                        title: 'navigation_user_readiness',
                        url: '/user-readiness',
                    },
                    {
                        title: 'navigation_user_completion',
                        url: '/user-completion',
                    },
                    {
                        title: 'navigation_user_activity',
                        url: '/user-activity',
                    },
                    {
                        title: 'navigation_user_exercise_history',
                        url: '/user-exercise-history',
                    }
                ],
            },
            {
                title: 'navigation_workout',
                color: '#CFDAF6',
                icon: 'assets/icons/ic_workout.svg',
                iconHome: 'assets/icons/ic_workout_home.svg',
                opened: false,
                items: [
                    {
                        title: 'navigation_exercises',
                        url: '/exercise',
                    },
                    {
                        title: 'navigation_workouts',
                        url: '/training',
                    },
                    {
                        title: 'navigation_programs',
                        url: '/program',
                    }
                    ,
                    {
                        title: 'navigation_tailored_programs',
                        url: '/program-tailor-made',
                    }
                ]
            }
        ];
    }

    private _navigation: SidebarNavigation[] = [];
    private _pinApp: string;
    private _workoutReadinessQuestionnaireId?: number = null;
    private _programReadinessQuestionnaireId?: number = null;
    private _isRoot = false;
    private _isOwner = false;
    private _isAdministrator = false;
    private _isCoach = false;
    private _enableUserHealthTracking = false;
    private _enableClientMessaging = false;
    private _enableGroupChat = false;
    private _isBlockedFromPosting = false;
    private _enableScheduler = false;
    private _enableReferrals = false;
    private _enableChallenges = false;
    private _enableCoaching = false;
    private _enablePaidProgramAssignment = false;
    private _enableSettingDefaultUserFolder = false;
    private _enableCoachingCheckoutPagesSetup = false;
    private _allowHidingCourseLinksFromTeamMembers = false;
    private _ownerWorkoutsAreHiddenFromCoaches = false;
    private _ownerProgramsAreHiddenFromCoaches = false;
    private _allowCoachesToManageOwnTeams = false;
    private _enableTeams = false;
    private _enableGiftCards = false;
    private _inAppPromoCodesAvailability: InAppPromoCodeAvailability = emptyInAppPromoCodeAvailability();
    private _enableAppTryout = false;
    private _enableReferralCodes = false;
    private _enableUserProgression = false;
    private _enableOutgoingWebhooks = false;
    private _userDateFormat = 'MM/dd/yyyy';
}
